import { getPrivateSessionState } from "@/lib/api/private-session";
import {
  MutationCache,
  QueryClient,
  type DefaultOptions,
  type Query,
} from "@tanstack/react-query";
import { toast } from "@a2z/ui";
import { keys } from "@/lib/i18n/portal";
import { translateStandalone } from "@/lib/i18n/standalone";
import { apiErrorMessage } from "@/lib/api/error-copy";
import { ApiJsonClientError } from "./api-json-client";

// Opt a mutation out of the global error toast (it owns its own error UX).
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      skipGlobalErrorToast?: boolean;
    };
  }
}

/**
 * Registration and quick-setup own their own error handling: every step is
 * best-effort and re-editable later in Settings, so a failure there is not
 * something the seller caused or can act on. Those surfaces mute the global
 * toast for as long as they are mounted rather than opting out mutation by
 * mutation — the persist chain touches eight different shared hooks, and a
 * ninth added later would silently start toasting again.
 *
 * Counted rather than boolean so overlapping mounts can't unmute each other.
 */
let globalErrorToastMutes = 0;

export function muteGlobalErrorToasts(): () => void {
  globalErrorToastMutes += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    globalErrorToastMutes = Math.max(0, globalErrorToastMutes - 1);
  };
}

/**
 * A failure is worth retrying only if repeating the identical call could plausibly
 * succeed: transport failures (offline, timeout, DNS) and 5xx. Deterministic
 * client errors (401/403/404 and other 4xx) would just re-emit the same error.
 * Mirrors the query-retry policy below.
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof ApiJsonClientError) {
    if (error.status === 401 || error.status === 403 || error.status === 404) {
      return false;
    }
    return error.status >= 500;
  }
  // Non-API errors are almost always network/transport faults → retryable.
  return true;
}

function shouldRetryQuery(failureCount: number, error: unknown) {
  if (error instanceof DOMException && error.name === "AbortError") return false;
  if (failureCount >= 1) {
    return false;
  }

  if (error instanceof ApiJsonClientError) {
    if (error.status === 401 || error.status === 403 || error.status === 404) {
      return false;
    }

    return error.status >= 500;
  }

  return true;
}

function shouldRefetchOnMount(query: Query) {
  if (query.state.data === undefined) {
    return "always" as const;
  }

  return query.state.isInvalidated;
}

export const portalQueryDefaults: DefaultOptions = {
  queries: {
    staleTime: 30_000,
    gcTime: 15 * 60_000,
    retry: shouldRetryQuery,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: shouldRefetchOnMount,
  },
  mutations: {
    retry: false,
  },
};

// One global error toast for every mutation, so call sites don't each wire their
// own. Transient failures also get a Retry that re-runs the mutation. Mutations
// with bespoke inline/silent/custom error UX opt out via
// `meta: { skipGlobalErrorToast: true }`.
const portalMutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    if (error instanceof DOMException && error.name === "AbortError") return;
    if (mutation.meta?.skipGlobalErrorToast || globalErrorToastMutes > 0) {
      return;
    }

    // Subscription-restricted writes are never worth a Retry — the block
    // lifts by resubscribing, not by repeating the call.
    if (
      error instanceof ApiJsonClientError &&
      error.code === "SELLER_SUBSCRIPTION_RESTRICTED"
    ) {
      toast.error(apiErrorMessage(error));
      return;
    }

    const generation = getPrivateSessionState().generation;
    const retryable = isRetryableError(error);
    const message = apiErrorMessage(error);

    toast.error(
      message,
      retryable ? translateStandalone(keys.errors.connectionIssue) : undefined,
      retryable
        ? {
            action: {
              label: translateStandalone(keys.common.retry),
              onClick: () => {
                if (generation !== getPrivateSessionState().generation) return;
                void mutation.execute(mutation.state.variables);
              },
            },
          }
        : undefined,
    );
  },
});

export function createPortalQueryClient() {
  return new QueryClient({
    defaultOptions: portalQueryDefaults,
    mutationCache: portalMutationCache,
  });
}
